import React from 'react';
import { basePaths } from 'app-constants';
import { TrackedLink } from 'components/common/TrackedLink';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { useTabbableClick } from './useTabbableClick';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';

type StatuteType = 'code' | 'constitution' | 'constitution-node';

interface BaseStatuteLinkProps extends LinkHelperProps {
  type?: StatuteType;
  citator?: boolean;
}

interface CAStatuteLinkProps extends BaseStatuteLinkProps {
  type?: 'code';
  jurisdictionId: string;
  codeJudicataId?: string;
  sectionBegin?: string;
  sectionEnd?: string;
  citator?: boolean;
}

interface CAConstitutionLinkProps extends BaseStatuteLinkProps {
  type?: 'constitution';
  article?: string;
  section?: string;
  citator?: boolean;
}

type StatuteLinkProps = CAStatuteLinkProps | CAConstitutionLinkProps;

export function useStatutePath(props: StatuteLinkProps) {
  const workspacePath = useWorkspacePath();

  const path = React.useMemo(() => {
    const parts: [string | undefined] = [basePaths.statutes];

    switch (props.type) {
      case 'constitution':
        if ('article' in props && props.article) {
          parts.push(
            'ca',
            'const',
            props.article,
            props.section,
            props.citator ? 'citator' : undefined,
          );
        }
        break;

      default:
        if (
          'jurisdictionId' in props &&
          'codeJudicataId' in props &&
          props.jurisdictionId &&
          props.codeJudicataId
        ) {
          parts.push(props.jurisdictionId, 'codes', props.codeJudicataId);
          if ('sectionBegin' in props && props.sectionBegin)
            parts.push(props.sectionBegin);
          if ('sectionEnd' in props && props.sectionEnd)
            parts.push(props.sectionEnd);
          if (props.citator) parts.push('citator');
        }
    }

    const basePath = parts.filter(Boolean).join('/');
    return workspacePath + basePath;
  }, [props, workspacePath]);

  return useQueryAndHash(path, props.query, props.hash);
}

export function StatuteLink(props: StatuteLinkProps) {
  const {
    type,
    article,
    section,
    jurisdictionId,
    codeJudicataId,
    sectionBegin,
    sectionEnd,
    onClick,
    tabbable,
    citator,
    query,
    hash,
    ...restProps
  } = props;
  const path = useStatutePath({
    type,
    article,
    section,
    citator,
    jurisdictionId,
    codeJudicataId,
    sectionBegin,
    sectionEnd,
    query,
    hash,
  });
  const handleClick = useTabbableClick(path.pathname, tabbable, onClick);

  return (
    <TrackedLink
      to={props.to ? props.to : path}
      onClick={handleClick}
      {...restProps}
    />
  );
}

// Convenience component for constitution links
export function ConstitutionLink(props: Omit<CAConstitutionLinkProps, 'type'>) {
  return <StatuteLink type="constitution" {...props} />;
}
